"use client";
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { generalGetRequest } from "@/app/config/APIConfig";
import { screenSizeContext } from "@/app/store/context/screenSizesContext";
import dynamic from "next/dynamic";

const SlidePopup = dynamic(
	() => import("@/app/components/shared/Popup/SlidePopup"),
	{ ssr: false }
);
const GetInTouch = dynamic(
	() => import("@/app/components/shared/form/GetInTouch"),
	{ ssr: false }
);

type GetInTouchPopupContextType = {
    isGetInTouchPopupOpened: boolean;
    openGetInTouchPopup: () => void;
    closeGetInTouchPopup: () => void;
};

const GetInTouchPopupContext = createContext<GetInTouchPopupContextType | undefined>(undefined);

export const GetInTouchPopupProvider = ({ children }: { children: ReactNode }) => {
    const [isGetInTouchPopupOpened, setIsGetInTouchPopupOpened] = useState(false);
    const [popupMounted, setPopupMounted] = useState(false);
    const [masterplanSelectedNeighborhood, setMasterplanSelectedNeighborhood] = useState<string>("");
    const [destinationData, setDestinationData] = useState([]);
    const { isIpad } = useContext(screenSizeContext);

    const { data: destinationsResp, isSuccess } = useQuery({
        queryKey: ["cities"],
        queryFn: () => generalGetRequest("api/cities"),
        enabled: isGetInTouchPopupOpened || popupMounted,
    });

    useEffect(() => {
        if (isGetInTouchPopupOpened) setPopupMounted(true);
    }, [isGetInTouchPopupOpened]);

    useEffect(() => {
        if (isSuccess) {
            setDestinationData(destinationsResp.data.data.cities[0].destinations);
        }
    }, [isSuccess, destinationsResp]);

    const openGetInTouchPopup = () => {
        if (isIpad) {
            document.documentElement.style.overflow = "hidden";
        }
        setIsGetInTouchPopupOpened(true);
    };

    const closeGetInTouchPopup = () => {
        if (isIpad) {
            document.documentElement.style.overflow = "auto";
        }
        // Note: setIsGetInTouchPopupOpened(false) is called by the SlidePopup component
    };

    return (
        <GetInTouchPopupContext.Provider
            value={{
                isGetInTouchPopupOpened,
                openGetInTouchPopup,
                closeGetInTouchPopup,
            }}
        >
            {children}
            {popupMounted && (
                <SlidePopup
                    customClassWrapper=""
                    setIsOpen={setIsGetInTouchPopupOpened}
                    isOpen={isGetInTouchPopupOpened}
                    onClose={closeGetInTouchPopup}
                >
                    <GetInTouch
                        destinationData={destinationData}
                        masterplanSelectedNeighborhood={masterplanSelectedNeighborhood}
                        setMasterplanSelectedNeighborhood={setMasterplanSelectedNeighborhood}
                        NeighborhoodsPage
                        className="h-fit lg:h-full"
                    />
                </SlidePopup>
            )}
        </GetInTouchPopupContext.Provider>
    );
};

export const useGetInTouchPopupContext = () => {
    const context = useContext(GetInTouchPopupContext);
    if (context === undefined) {
        throw new Error("useGetInTouchPopupContext must be used within a GetInTouchPopupProvider");
    }
    return context;
};
